import React, { useCallback } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import {
  PiPlusLight,
  PiTrashLight,
  PiPencilSimpleLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import {
  deleteGlobalRule,
} from '../../store/actions/Review/ReviewActions';
import { GlobalRule, FindingSeverity } from '../../types/review';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, globalRulesTourSteps } from '../shared/FeatureTour';

const severityColors: Record<FindingSeverity, BadgeColor> = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow',
  info: 'blue',
};

const severityLabels: Record<FindingSeverity, string> = {
  critical: 'Critical',
  major: 'Major',
  minor: 'Minor',
  info: 'Info',
};

interface ReviewGlobalRulesProps {
  reviewUuid: string;
  globalRules: GlobalRule[];
  readOnly?: boolean;
}

const ReviewGlobalRules: React.FC<ReviewGlobalRulesProps> = ({ reviewUuid, globalRules, readOnly }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'global-rules',
    steps: globalRulesTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  // ─── Side panel handlers ─────────────────────────────────────────────

  const handleAddRule = useCallback(() => {
    dispatch(showSidePanel({
      type: 'editGlobalRule',
      showSidePanel: true,
      size: 'md',
      message: t('New Global Rule'),
      reviewUuid,
      actions: [
        { label: t('Save Rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleEdit = useCallback((rule: GlobalRule) => {
    dispatch(showSidePanel({
      type: 'editGlobalRule',
      showSidePanel: true,
      size: 'md',
      message: t('Edit Global Rule'),
      reviewUuid,
      rule,
      actions: [
        { label: t('Update Rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleDelete = useCallback((ruleUuid: string) => {
    dispatch(
      showModal({
        type: 'confirmation',
        message: t('Delete Global Rule'),
        subMsg: t('This will permanently remove this global rule from the playbook. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteGlobalRule(reviewUuid, ruleUuid) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  return (
    <div className="pb-10 space-y-0">
      {/* Filter bar with add button */}
      <div data-tour-id="global-rules-header" className="pt-4 px-2">
        <FilterBarWithTabs
          actions={!readOnly ? [
            {
              label: t('Add Rule'),
              onClick: handleAddRule,
              icon: <PiPlusLight className="w-4 h-4" />,
            },
          ] : []}
          noParentPadding
          noBorder
        />
      </div>

      <div className="px-4 pt-4 space-y-2">
      {/* Rules list */}
      {globalRules.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-hairline rounded-lg">
          <p className="text-sm text-fg-muted">
            {readOnly
              ? t('No global rules defined for this playbook.')
              : t('No global rules yet. Add cross-cutting rules that apply to the entire agreement.')}
          </p>
          {!readOnly && (
            <Button
              variant="neutral"
              size="small"
              className="mt-3"
              onClick={handleAddRule}
            >
              <PiPlusLight className="w-4 h-4 me-1" />
              {t('Add First Rule')}
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-tour-id="global-rules-list">
          {globalRules.map((rule) => {
            const borderColor: Record<string, string> = {
              critical: 'border-l-red-500',
              major: 'border-l-orange-500',
              minor: 'border-l-yellow-400',
              info: 'border-l-blue-400',
            };

            return (
              <div
                key={rule.uuid}
                className={clsx(
                  'flex items-start gap-3 p-3 glass border-s-4 hover:shadow-sm transition-all duration-200 ease-slide',
                  borderColor[rule.severity] || 'border-l-hairline',
                )}
              >
                <Badge color={severityColors[rule.severity]} className="shrink-0 mt-0.5">
                  {severityLabels[rule.severity]}
                </Badge>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-fg-body leading-relaxed line-clamp-3 max-w-3xl">{rule.rule_text}</p>
                </div>
                {!readOnly && (
                  <div className="flex items-center gap-0.5 shrink-0 ms-auto">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(rule); }}
                      className="p-1 text-fg-muted hover:text-primary-600 rounded transition-colors"
                      title={t('Edit')}
                    >
                      <PiPencilSimpleLight className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(rule.uuid); }}
                      className="p-1 text-fg-muted hover:text-danger-500 rounded transition-colors"
                      title={t('Delete')}
                    >
                      <PiTrashLight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </div>{/* end content area */}

      <FeatureTour
        steps={globalRulesTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default ReviewGlobalRules;
